<template>
	<div
		:class="{
			'thumbnail--selected' : selected,
			'thumbnail--loading' : loading,
			'thumbnail--can-be-selected' : (canBeSelected || selected),
		}"
		class="thumbnail"
		data-cy="thumbnail"
		tabindex="0"
		:aria-label="ariaLabel"
		@click.stop="$emit('toggle', $event)"
		@keyup.enter="$emit('toggle', $event)"
		@keyup.space="$emit('toggle', $event)"
		@mouseover="isHover = true"
		@mouseleave="isHover = false"
	>
		<transition name="fade">
			<div
				v-if="!loading"
				key="notLoading"
			>
				<div
					:style="`background-image: url(${media.thumbnail})`"
					class="thumbnail__photo"
				/>

				<div class="thumbnail__options">
					<helio-icon
						:class="{
							'thumbnail__option--view' : (type === 'image'),
							'thumbnail__option--play' : (type === 'video'),
						}"
						:token="type === 'video' ? 'play' : 'eye'"
						:weight="type === 'video' ? 'light' : 'regular'"
						class="thumbnail__option thumbnail__option--left"
						@click.stop="$emit('preview')"
					/>
					<helio-icon
						v-if="!beingDeleted"
						class="thumbnail__option thumbnail__option--left"
						token="trash-alt"
						weight="light"
						:data-anchor="`cantDeleteTooltip-${media.id}`"
						@click.stop="deleteMedia"
					/>
					<helio-spinner
						v-if="beingDeleted"
						size="x-small"
						color="white"
					/>
					<helio-icon
						v-show="!selected && canBeSelected"
						class="thumbnail__option thumbnail__option--right"
						token="circle"
						weight="light"
						@click.stop="$emit('toggle')"
					/>
					<helio-icon
						v-show="selected"
						class="thumbnail__option thumbnail__option--right"
						token="check-circle"
						weight="light"
						@click.stop="$emit('toggle')"
					/>
					<helio-tooltip
						:active="showCantDeleteTooltip && isHover"
						:anchor="`cantDeleteTooltip-${media.id}`"
						class="thumbnail__cant-delete-tooltip"
						position="top"
						skin="dark"
					>
						<template v-slot:content>
							{{ $t('upload.thumbnail.cant-delete') }}
						</template>
					</helio-tooltip>
				</div>

				<div class="thumbnail__informations">
					<label
						v-if="media.name"
						class="thumbnail__name"
					>
						<v-clamp
							:max-lines="1"
							autoresize
						>
							{{ media.name }}
						</v-clamp>
					</label>
					<p
						v-if="media[extra]"
						class="thumbnail__extra-information"
					>
						<helio-icon
							v-if="type === 'video'"
							token="user"
							weight="light"
						/>

						<v-clamp
							:max-lines="1"
							autoresize
						>
							{{ media[extra] }}
						</v-clamp>
					</p>
				</div>

				<div class="thumbnail__checked">
					<div class="thumbnail__checked-bg" />

					<helio-icon
						token="check-circle"
						weight="bold"
					/>
				</div>
			</div>
			<div
				v-if="loading"
				key="whileLoading"
				class="thumbnail__loading"
			>
				<helio-spinner
					color="blue"
				/>
			</div>
		</transition>
	</div>
</template>

<script>
import VClamp from 'vue-clamp';
import Spinner from '../../spinner/Spinner.vue';
import Icon from '../../icon/Icon.vue';
import Tooltip from '../../tooltip/Tooltip.vue';
import Centios from '../../../utils/communication/Centios';

export default {
	components: {
		'helio-icon': Icon,
		'helio-tooltip': Tooltip,
		'helio-spinner': Spinner,
		VClamp,
	},
	props: {
		selected: {
			type: Boolean,
			required: false,
			default: null,
		},
		canBeSelected: {
			type: Boolean,
			required: false,
			default: null,
		},
		media: {
			type: Object,
			required: false,
			default: null,
		},
		extra: {
			type: String,
			required: false,
			default: null,
		},
		type: {
			type: String,
			required: true,
		},
		api: {
			type: String,
			required: true,
		},
		initialLoading: {
			type: Boolean,
			required: false,
			default: false,
		},
	},
	data() {
		return {
			loading: false,
			beingDeleted: false,
			showCantDeleteTooltip: false,
			isHover: false,
		};
	},
	computed: {
		ariaLabel() {
			if (this.media === null) {
				return '';
			}
			let label = `${this.media.name}: `;

			if (this.selected) {
				label += this.$t('aria-label.selected');
			} else {
				label += this.$t('aria-label.not-selected');
			}

			return label;
		},
	},
	created() {
		if (this.initialLoading) {
			this.loading = true;
		}
	},
	methods: {
		deleteMedia() {
			this.beingDeleted = true;

			const endpoint = `${this.api}/medias/${this.media.id}`;

			Centios.submit('delete', endpoint).then((response) => {
				const { data } = response;

				this.beingDeleted = false;

				if (data.code === 200) {
					this.$emit('delete', this.media);
				} else if (data.code === 403) {
					this.showCantDeleteTooltip = true;

					setTimeout(() => {
						this.showCantDeleteTooltip = false;
					}, 5000);
				}
			});
		},
	},
};
</script>

<style lang="scss" scoped src="./thumbnail.scss" />
